/**
 * A tiny retargetable tween engine.
 *
 * Why not CSS transitions or the Web Animations API here?
 * The dock retargets width/height/scale on *every* pointer frame. A transition
 * restarts on each style write (producing lag), and WAAPI would mean allocating
 * a new Animation object 60x/second per item. Instead each dock item owns ONE
 * long-lived "animatable" whose channels can be re-aimed at a new value at any
 * time; the tween re-bases from wherever it currently is, so motion stays
 * continuous.
 *
 * All animatables share a SINGLE requestAnimationFrame loop. Twelve dock items
 * therefore cost one rAF callback per frame, not twelve.
 */

type Easing = (t: number) => number;

/* ------------------------------------------------------------------ easing */

const BEZIER_EPSILON = 1e-6;

/** Cubic-bezier solver (Newton-Raphson with bisection fallback). */
export function cubicBezier(x1: number, y1: number, x2: number, y2: number): Easing {
  if (x1 === y1 && x2 === y2) return (t) => t;

  const a = (v1: number, v2: number) => 1 - 3 * v2 + 3 * v1;
  const b = (v1: number, v2: number) => 3 * v2 - 6 * v1;
  const c = (v1: number) => 3 * v1;

  const calc = (t: number, v1: number, v2: number) =>
    ((a(v1, v2) * t + b(v1, v2)) * t + c(v1)) * t;
  const slope = (t: number, v1: number, v2: number) =>
    3 * a(v1, v2) * t * t + 2 * b(v1, v2) * t + c(v1);

  return (x: number) => {
    if (x <= 0) return 0;
    if (x >= 1) return 1;

    let t = x;
    for (let i = 0; i < 8; i += 1) {
      const currentSlope = slope(t, x1, x2);
      if (Math.abs(currentSlope) < BEZIER_EPSILON) break;
      const currentX = calc(t, x1, x2) - x;
      t -= currentX / currentSlope;
    }

    // Guard against Newton diverging outside [0, 1].
    if (t < 0 || t > 1) {
      let low = 0;
      let high = 1;
      t = x;
      while (high - low > BEZIER_EPSILON) {
        if (calc(t, x1, x2) < x) low = t;
        else high = t;
        t = (low + high) / 2;
      }
    }

    return calc(t, y1, y2);
  };
}

const NAMED_EASINGS: Record<string, Easing> = {
  'linear': (t) => t,
  'ease': cubicBezier(0.25, 0.1, 0.25, 1),
  'ease-in': cubicBezier(0.42, 0, 1, 1),
  'ease-out': cubicBezier(0, 0, 0.58, 1),
  'ease-in-out': cubicBezier(0.42, 0, 0.58, 1),
};

const easingCache = new Map<string, Easing>();

/** Accepts `linear`, `ease-*`, or a `cubic-bezier(a, b, c, d)` string. */
export function resolveEasing(input: string | undefined, fallback = 'linear'): Easing {
  const raw = (input && input.trim()) || fallback;
  const cached = easingCache.get(raw);
  if (cached) return cached;

  let easing: Easing | undefined = NAMED_EASINGS[raw];

  if (!easing && raw.startsWith('cubic-bezier(')) {
    const numbers = raw
      .slice('cubic-bezier('.length, raw.lastIndexOf(')'))
      .split(',')
      .map((part) => Number(part.trim()));
    if (numbers.length === 4 && numbers.every((n) => Number.isFinite(n))) {
      easing = cubicBezier(numbers[0]!, numbers[1]!, numbers[2]!, numbers[3]!);
    }
  }

  easing ??= NAMED_EASINGS[fallback] ?? NAMED_EASINGS.linear!;
  easingCache.set(raw, easing);
  return easing;
}

/* ------------------------------------------------------------------ ticker */

interface Runner {
  tick(now: number): boolean;
  render(): void;
}

const runners = new Set<Runner>();
let frameId: number | null = null;

function loop(now: number) {
  frameId = null;
  for (const runner of Array.from(runners)) {
    const alive = runner.tick(now);
    runner.render();
    if (!alive) runners.delete(runner);
  }
  if (runners.size > 0) schedule();
}

function schedule() {
  if (frameId !== null || typeof requestAnimationFrame === 'undefined') return;
  frameId = requestAnimationFrame(loop);
}

/* -------------------------------------------------------------- animatable */

interface ChannelState {
  duration: number;
  easing: Easing;
  from: number;
  startedAt: number;
  target: number;
}

export interface AnimatableOptions<T extends Record<string, number>> {
  /** Per-key defaults used when `set()` is called without an explicit duration. */
  defaults?: Partial<Record<keyof T, {duration?: number; easing?: string}>>;
  /** Called once per frame while any channel is moving, and once when it settles. */
  onRender: (state: T) => void;
}

export interface Animatable<T extends Record<string, number>> {
  /** Current (interpolated) values. Mutated in place — do not hold references. */
  readonly state: T;
  /** Re-aim a channel. Motion re-bases from the current value, never restarts. */
  set(key: keyof T, value: number, duration?: number, easing?: string): void;
  /** Jump a channel to a value with no animation. */
  snap(key: keyof T, value: number): void;
  /** Stop animating and release the ticker slot. */
  dispose(): void;
}

export function createAnimatable<T extends Record<string, number>>(
  initial: T,
  options: AnimatableOptions<T>,
): Animatable<T> {
  const state = {...initial} as T;
  const channels = new Map<keyof T, ChannelState>();
  let disposed = false;
  let dirty = false;

  const runner: Runner = {
    tick(now) {
      for (const [key, channel] of Array.from(channels.entries())) {
        const elapsed = now - channel.startedAt;
        if (channel.duration <= 0 || elapsed >= channel.duration) {
          (state as Record<keyof T, number>)[key] = channel.target;
          channels.delete(key);
        } else {
          const progress = channel.easing(elapsed / channel.duration);
          (state as Record<keyof T, number>)[key] =
            channel.from + (channel.target - channel.from) * progress;
        }
        dirty = true;
      }
      return channels.size > 0;
    },
    render() {
      if (!dirty) return;
      dirty = false;
      options.onRender(state);
    },
  };

  return {
    state,
    set(key, value, duration, easing) {
      if (disposed || !Number.isFinite(value)) return;

      const fallback = options.defaults?.[key];
      const resolvedDuration = duration ?? fallback?.duration ?? 0;
      const resolvedEasing = resolveEasing(easing ?? fallback?.easing, 'linear');
      const current = state[key] as number;

      if (Math.abs(current - value) < 1e-4) {
        const running = channels.get(key);
        if (running && Math.abs(running.target - value) < 1e-4) return;
        if (!running) return;
      }

      if (resolvedDuration <= 0) {
        (state as Record<keyof T, number>)[key] = value;
        channels.delete(key);
        dirty = true;
        runners.add(runner);
        schedule();
        return;
      }

      channels.set(key, {
        duration: resolvedDuration,
        easing: resolvedEasing,
        from: current,
        // performance.now() shares its time base with rAF timestamps.
        startedAt: performance.now(),
        target: value,
      });
      runners.add(runner);
      schedule();
    },
    snap(key, value) {
      if (disposed || !Number.isFinite(value)) return;
      channels.delete(key);
      (state as Record<keyof T, number>)[key] = value;
      dirty = true;
      options.onRender(state);
    },
    dispose() {
      disposed = true;
      channels.clear();
      runners.delete(runner);
    },
  };
}
